import { api } from './api';
import { DynamicConfigChange, DynamicConfigChangeRequest, DynamicConfigEntry } from '../types/dynamicConfig';

/**
 * Runtime config, served by the **platform** service under `/api/admin/dynamic-config/**`
 * (gateway route `platform-admin-dynamic-config`).
 *
 * #1113 — access is per key: each entry's `adminScreen` names the screen that may read and write it
 * (`dynamic-config`, `finance-config` or `finance`). The gateway admits the collection path for any of
 * the three, and platform filters the list and refuses a key outside the caller's screens with 403
 * `CONFIG_KEY_FORBIDDEN` (the body names the `key`).
 *
 * A change reaches the running services within about a minute — each polls platform on that
 * interval. Nothing restarts, and nothing already in flight is recomputed.
 */
export const dynamicConfigApi = {
  /** Every key the caller may access, overridden or not, ordered by owning service. */
  list: (token: string) =>
    api.get<DynamicConfigEntry[]>('/api/admin/dynamic-config', token),

  /**
   * Sets an override. **400** when the value does not fit the key's declared type or bounds — the
   * message names what was expected. **404** for a key no service reads.
   */
  set: (key: string, value: string, token: string) =>
    api.put<DynamicConfigEntry>(
      `/api/admin/dynamic-config/${encodeURIComponent(key)}`, { value }, token),

  /**
   * #1113 — sets several overrides in one atomic request (1–20 keys, each once). Every change is
   * validated before anything is written: one bad (400) or forbidden (403) key writes nothing, and
   * the error body's `key` names it (`ApiError.key`). An unchanged value writes no audit row.
   * Returns the requested keys as stored.
   */
  setMany: (changes: DynamicConfigChangeRequest[], token: string) =>
    api.patch<DynamicConfigEntry[]>('/api/admin/dynamic-config', { changes }, token),

  /** Removes the override, handing the key back to the value its service was deployed with. */
  clear: (key: string, token: string) =>
    api.delete<void>(`/api/admin/dynamic-config/${encodeURIComponent(key)}`, token),

  /** The 50 most recent changes to one key, newest first. */
  history: (key: string, token: string) =>
    api.get<DynamicConfigChange[]>(
      `/api/admin/dynamic-config/${encodeURIComponent(key)}/history`, token),
};
